import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import axiosInstance from "../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import { Input, Table, Tag, Tooltip } from "antd"
import type { ColumnsType } from "antd/es/table/interface"
import UserSubscriptionFormModal from "./UserSubscriptionFormModal"
import { ModePage } from "common/enum"
import processError from "../../../../utils/processError"
import useTableConfig from "../../../../hooks/useTableConfig"
import dayjs from "dayjs"
import {UserSubscriptionResponse} from "../../../../model/response";

export default function UserSubscriptionManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig()
  const [userId, setUserId] = useState<string>("")
  const [subscriptionId, setSubscriptionId] = useState<string>("")
  const [mode, setMode] = useState<ModePage>(ModePage.ADD)
  const [isOpen, setOpenModal] = useState<boolean>(false)

  const [data, setData] = useState<UserSubscriptionResponse[]>()
  const [loading, setLoading] = useState(false)

  const formatDate = (date: string) => {
    return dayjs(date).format("DD/MM/YYYY HH:mm")
  }

  const getStatusColor = (isEnable: boolean, expirationDate: string) => {
    if (!isEnable) return "red"
    const now = dayjs()
    const expiry = dayjs(expirationDate)
    if (expiry.isBefore(now)) return "orange"
    return "green"
  }

  const getStatusText = (isEnable: boolean, expirationDate: string) => {
    if (!isEnable) return "Không hoạt động"
    const now = dayjs()
    const expiry = dayjs(expirationDate)
    if (expiry.isBefore(now)) return "Hết hạn"
    return "Hoạt động"
  }

  const columns: ColumnsType<UserSubscriptionResponse> = [
    {
      title: "User ID",
      dataIndex: "userId",
      width: "15%",
      align: "center",
      render: (text) => (
        <Tooltip title={text}>
          <div className="max-w-[100px] truncate">{text}</div>
        </Tooltip>
      ),
    },
    {
      title: "Gói",
      dataIndex: "plan",
      width: "10%",
      align: "center",
      render: (plan) => (
        <Tooltip title={plan?.id}>
          <div className="max-w-[80px] truncate">{plan?.id || "-"}</div>
        </Tooltip>
      ),
    },
    {
      title: "Điểm miễn phí",
      dataIndex: "pointFree",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("pointFree"),
      render: (value, record) => (
        <span className={record.pointFreeEnable ? "text-green-600" : "text-gray-400"}>
          {value} {record.pointFreeEnable ? "✓" : "✗"}
        </span>
      ),
    },
    {
      title: "Điểm premium",
      dataIndex: "pointPremium",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("pointPremium"),
      render: (value, record) => (
        <span className={record.pointPremiumEnable ? "text-green-600" : "text-gray-400"}>
          {value} {record.pointPremiumEnable ? "✓" : "✗"}
        </span>
      ),
    },
    {
      title: "Ngày đăng ký",
      dataIndex: "subscriptionDate",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("subscriptionDate"),
      render: (date) => formatDate(date),
    },
    {
      title: "Ngày hết hạn",
      dataIndex: "expirationDate",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("expirationDate"),
      render: (date) => formatDate(date),
    },
    {
      title: "Trạng thái",
      dataIndex: "isEnable",
      align: "center",
      render: (isEnable, record) => (
        <Tag color={getStatusColor(isEnable, record.expirationDate)}>
          {getStatusText(isEnable, record.expirationDate)}
        </Tag>
      ),
      sorter: true,
      sortOrder: getColumnSorter("isEnable"),
    },
    {
      title: "Hành động",
      dataIndex: "action",
      align: "center",
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
        </div>
      ),
    },
  ]

  const moveEditView = async (e: any, record: UserSubscriptionResponse) => {
    setMode(ModePage.EDIT)
    setOpenModal(true)
    setSubscriptionId(record.id)
  }

  const loadListSubscriptions = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance({
        url: "subscription/page",
        params: {
          userId,
          size: pageSize,
          page: currentPage,
          sortBy,
          direction,
          fromCMS: true,
        },
      })

      if (res.status === 200) {
        setData(res.data.content)
        setTotalElements(res.data.totalElements)
      }
    } catch (err) {
      processError(err)
    }
    setLoading(false)
  }

  const onSearch = (e: any) => {
    setUserId(e.target.value)
  }

  const addNewSubscription = async () => {
    setMode(ModePage.ADD)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleBeforeSuccess = () => {
    setOpenModal(false)
    loadListSubscriptions().then()
  }

  useEffect(() => {
    loadListSubscriptions().then()
  }, [currentPage, pageSize, sortBy, direction, userId])

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewSubscription()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Input placeholder="Nhập User ID để tìm kiếm" name="userId" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </div>
      <UserSubscriptionFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        subscriptionId={subscriptionId}
      />
    </div>
  )
}
